/**
 * Renders a pop-up dialog.
 * 
 * @prop {object} content
 * @prop {function} [onCancel]
 * @prop {function} [onChange] - Optional callback for options change event
 * @prop {function} [onConfirm]
 * @prop {function} [onTimeout]
 * @prop {array} [options]
 * @prop {integer} [timeout]
 * @prop {string} [type]
 */

import React from 'react';

// Helpers
import { focusable } from '../../helpers/helpers';

const Dialog = props => {
  // Get values from props
  const {
    content,
    onCancel,
    onClose,
    onConfirm,
    store,
    type
  } = props;
  const { AppStore } = store;

  // Set up state
  const [focusIndex, setFocusIndex] = React.useState(0);

  // Create refs
  const dialogRoot = React.useRef(null);

  // Create an array to hold focusable child elements.
  let _focusableElements = [];

  // Main useEffect hook
  React.useEffect(() => {
    // Add focusable child elements to
    // the array and focus the first one.
    if (!_focusableElements.length) {
      _focusableElements = dialogRoot.current.querySelectorAll(focusable);
    }

    _focusableElements[focusIndex].focus();
  }, [focusIndex])

  /**
   * Focus appropriate elements
   * 
   * @function focusElement
   * @param {integer} index
   */
  const focusElement = index => {
    setFocusIndex(index);
  }

  /**
   * Handle keyboard interaction
   * 
   * @function handleKeyDown
   * @param {event} e
   */
  const handleKeyDown = e => {
    const first = 0;
    const last = _focusableElements.length - 1;

    const ESCAPE = "Escape";
    const TAB = "Tab";
    const SHIFT = e.shiftKey;

    // Tab Key behavior
    if (e.key === TAB && !SHIFT) {
      e.preventDefault();
      
      if (focusIndex < last) {
        focusElement(focusIndex + 1);
      } else {
        focusElement(first);
      }
    }

    // Shift + Tab Key behavior
    if (e.key === TAB && SHIFT) {
      e.preventDefault();
      if (focusIndex > 0) {
        focusElement(focusIndex - 1);
      } else {
        focusElement(last);
      }
    }

    // Escape Key behavior
    if (e.key === ESCAPE) {
      handleCancel(e);
    }
  }

  /**
   * Handle dialog cancellation
   * 
   * @function handleCancel
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }

    handleClose();
  }

  /**
   * Handle dialog confirmation
   * 
   * @function handleConfirm
   */
  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }

    handleClose();
  }

  /**
   * Handle dialog close
   * 
   * @function handleClose
   */
  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    AppStore.closeDialog();
  }

  /**
   * Convey the urgency of the message to assistive technology
   * 
   * @function renderRole
   * @param {string} type
   * @returns {string}
   */
  const renderRole = type => {
    let role;

    switch (type) {
      case 'confirm':
      case 'alert':
      case 'error':
        role = 'alertdialog';
        break;

      case 'wide':
      case 'html':
      case 'info':
        role = 'dialog';
        break;

      default:
        role = 'dialog';
    }

    return role;
  }

  return (
    <div
      aria-describedby="dialog-description"
      aria-labelledby="dialog-title"
      aria-modal="true"
      className={`dialog${type ? ` dialog--${type}` : ''}`}
      id="dialog-root"
      onKeyDown={handleKeyDown}
      ref={dialogRoot}
      role={renderRole(type)}
    >
      <div
        className={`dialog__content${
          type ? ` dialog__content--${type}` : ""}
        `}
      >
        <div className="dialog__head">
          <h2 id="dialog-title" className="dialog__title">
            {content.heading}
          </h2>
        </div>

        <p id="dialog-description">{content.body}</p>

        <div className="dialog__action">
          {type === 'confirm' &&
            <button
              className="btn btn--outline"
              onClick={handleCancel}
            >
              {content.cancel}
            </button>
          }

          <button
            className="btn"
            onClick={handleConfirm}
          >
            {content.button}
          </button>
        </div>
      </div>
    </div>
  );
}

Dialog.displayName = 'Dialog';

export default Dialog;